import errorMonitoring, { setUser } from "lib/errorMonitoring";

interface ReduxStoreLike {
  getState: () => Record<string, any>;
}

type Next = (action: any) => unknown;

let reduxStoreRef: ReduxStoreLike | null = null;

export const getReduxState = () => {
  try {
    return reduxStoreRef?.getState() ?? null;
  } catch (error) {
    console.error("Failed to get Redux state", error);
    return null;
  }
};

export const cleanStateForErrorReporting = (state: unknown) => {
  try {
    const seen = new WeakSet<object>();

    const replacer = (_key: string, value: unknown) => {
      if (value === null || typeof value !== "object") {
        return value;
      }

      if (seen.has(value)) {
        return "[Circular]";
      }

      seen.add(value);
      return value;
    };

    const serialized = JSON.stringify(state, replacer);
    return JSON.parse(serialized);
  } catch (error) {
    console.error("Error monitoring middleware: Failed to clean state", error);
    return { error: "[State Cleaning Error]" };
  }
};

const errorMonitoringMiddleware =
  (store: ReduxStoreLike) => (next: Next) => (action: any) => {
    try {
      if (!reduxStoreRef) {
        reduxStoreRef = store;
      }

      const result = next(action);

      try {
        const state = store.getState();
        const currentUser = state?.currentUser;
        const router = state?.router;
        const currentTeam = state?.ui?.currentTeam;

        setUser(
          currentUser?.id
            ? {
                id: String(currentUser.id),
                email: currentUser.email,
                username: currentUser.name
              }
            : null
        );

        const actionType = action?.type || "[No Type]";
        let actionData = "";
        try {
          const cleanAction = { ...action } as Record<string | symbol, unknown>;
          delete cleanAction.type;
          delete cleanAction.onSuccess;
          delete cleanAction.onFailure;
          delete cleanAction[Symbol.for("@@redux-saga/SAGA_ACTION")];

          const keys = Object.keys(cleanAction);
          if (keys.length > 0) {
            const serialized = JSON.stringify(cleanAction);
            actionData =
              serialized.length > 1000
                ? `${serialized.substring(0, 1000)}... [truncated]`
                : serialized;
          }
        } catch {
          actionData = "[Cannot Serialize]";
        }

        errorMonitoring.addBreadcrumb({
          category: "redux",
          action: actionType,
          message: actionData || undefined,
          metadata: {
            action_type: actionType,
            user_id: String(currentUser?.id || "not_logged_in"),
            user_name: currentUser?.name || "not_logged_in",
            user_email: currentUser?.email || "not_logged_in",
            current_path: router?.location?.pathname || "",
            current_team_id: String(currentTeam?.id || ""),
            action_error: action?.error ? String(action.error) : "",
            timestamp: new Date().toISOString()
          }
        });
      } catch (breadcrumbError) {
        if (process.env.NODE_ENV === "development") {
          console.warn(
            "Error monitoring middleware: Failed to add breadcrumb",
            breadcrumbError
          );
        }
      }

      return result;
    } catch (error) {
      console.error("Error monitoring middleware: Unexpected error", error);
      return next(action);
    }
  };

export default errorMonitoringMiddleware;
